import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, CheckCircle, XCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { SectionHeader, EmptyState, StatusBadge, fmtDate } from "./ui";

export const CLIENT_TYPES = [["individual", "Individual"], ["business", "Business"], ["both", "Individual + Business"]];
export const STATUSES = ["Active", "Inactive", "Needs Attention", "Archived"];
export const SERVICE_OPTIONS = ["Business Formation", "Business Management", "Wealth Foundation Program", "Business Financial Solutions", "Tradeline", "Guaranteed Approval", "Document Management", "Corporate Management", "Special Profile", "Other"];
const EMPTY = { client_type: "individual", full_name: "", business_name: "", email: "", phone: "", address: "", city: "", state: "", zip_code: "", ssn: "", dob: "", status: "Active", service_package: "", services: [], emergency_contact: "", notes: "", portal_access: false };

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export function ServiceSelect({ value = [], onChange }) {
  const toggle = (s) => {
    const set = new Set(value);
    set.has(s) ? set.delete(s) : set.add(s);
    onChange([...set]);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="service-multiselect">
      {SERVICE_OPTIONS.map((s) => {
        const active = value.includes(s);
        return (
          <button
            key={s} type="button" onClick={() => toggle(s)} data-testid={`service-option-${slug(s)}`}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${active ? "border-primary bg-primary/15 text-primary" : "border-white/15 text-muted-foreground hover:border-white/30 hover:text-foreground"}`}
          >{s}</button>
        );
      })}
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [pending, setPending] = useState([]);
  const [tab, setTab] = useState("clients");
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([api.get("/cc/clients"), api.get("/cc/pending")]);
    setClients(c.data);
    setPending(p.data);
    setLoading(false);
  }, []);

  const approve = async (c) => {
    try { await api.post(`/cc/clients/${c.id}/approve`); toast.success("Client approved"); load(); }
    catch { toast.error("Failed to approve"); }
  };
  const reject = async (c) => {
    if (!window.confirm(`Reject and discard "${c.full_name || c.business_name || c.email}"?`)) return;
    try { await api.post(`/cc/clients/${c.id}/reject`); toast.success("Sign-up rejected"); load(); }
    catch { toast.error("Failed to reject"); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (sp.get("new") === "1") { setForm(EMPTY); setOpen(true); sp.delete("new"); setSp(sp, { replace: true }); }
    if (sp.get("tab") === "pending") { setTab("pending"); sp.delete("tab"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/cc/clients", form);
      toast.success("Client created");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const shown = clients.filter((c) =>
    !filter || (c.full_name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (c.business_name || "").toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="clients-page">
      <SectionHeader
        title="Clients"
        subtitle="Individuals, businesses, and combined profiles you manage."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-client-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> New Client</Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
              <DialogHeader><DialogTitle>New Client</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Add an individual, business, or combined client profile.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Client type</Label>
                  <Select value={form.client_type} onValueChange={(v) => set("client_type", v)}>
                    <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CLIENT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Full name</Label><Input data-testid="client-fullname-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
                  <div><Label>Business name</Label><Input data-testid="client-business-input" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
                  <div><Label>Email</Label><Input data-testid="client-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                  <div><Label>Phone</Label><Input data-testid="client-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                  <div><Label>Date of birth</Label><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></div>
                  <div>
                    <Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Street address</Label><Input data-testid="client-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input data-testid="client-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
                  <div><Label>State</Label><Input data-testid="client-state-input" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
                  <div><Label>Zip</Label><Input data-testid="client-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
                </div>
                <div><Label>Social Security Number</Label><Input data-testid="client-ssn-input" value={form.ssn} onChange={(e) => set("ssn", e.target.value)} placeholder="___-__-____" /></div>
                <div>
                  <Label>Services</Label>
                  <div className="mt-1"><ServiceSelect value={form.services} onChange={(v) => set("services", v)} /></div>
                </div>
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
                  <div><div className="text-sm font-medium">Client portal access</div><div className="text-xs text-muted-foreground">Give a limited dashboard you control</div></div>
                  <Switch checked={form.portal_access} onCheckedChange={(v) => set("portal_access", v)} data-testid="client-portal-switch" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-client-button" disabled={saving} onClick={save} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Create client"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex gap-2 mb-5">
        <Button size="sm" variant={tab === "clients" ? "default" : "ghost"} onClick={() => setTab("clients")} data-testid="tab-clients" className={tab === "clients" ? "bg-primary text-primary-foreground" : ""}>Clients</Button>
        <Button size="sm" variant={tab === "pending" ? "default" : "ghost"} onClick={() => setTab("pending")} data-testid="tab-pending" className={tab === "pending" ? "bg-primary text-primary-foreground" : ""}>
          Pending Approval{pending.length ? ` (${pending.length})` : ""}
        </Button>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> : tab === "clients" ? (
        <>
          <Input placeholder="Filter clients…" value={filter} onChange={(e) => setFilter(e.target.value)} className="mb-4 max-w-sm bg-zinc-900/60 border-white/10" data-testid="clients-filter-input" />
          {shown.length === 0 ? <EmptyState icon={Users} title="No clients yet" hint="Approve a pending sign-up or create a client to get started." /> : (
            <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="clients-list">
              {shown.map((c) => (
                <button key={c.id} onClick={() => navigate(`/command/clients/${c.id}`)} data-testid={`client-row-${c.id}`}
                  className="w-full flex items-center justify-between gap-4 px-4 py-4 text-left hover:bg-white/5 transition-colors">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.full_name || c.business_name || "Unnamed"}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {c.business_name && c.full_name ? `${c.business_name} · ` : ""}{c.email || c.phone || "—"} · added {fmtDate(c.date_added)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs text-muted-foreground capitalize hidden sm:inline">{c.client_type}</span>
                    <StatusBadge status={c.status} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        pending.length === 0 ? <EmptyState icon={CheckCircle} title="No pending sign-ups" hint="New public sign-ups will appear here for your approval." /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="pending-list">
            {pending.map((c) => (
              <div key={c.id} data-testid={`pending-row-${c.id}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
                <div className="min-w-0">
                  <div className="font-medium truncate">{c.full_name || c.business_name || "Unnamed"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[c.email, c.phone, c.service_package].filter(Boolean).join(" · ") || "—"} · {fmtDate(c.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" onClick={() => approve(c)} data-testid={`approve-client-${c.id}`} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1"><CheckCircle size={16} weight="bold" /> Approve</Button>
                  <Button size="sm" variant="ghost" onClick={() => reject(c)} data-testid={`reject-client-${c.id}`} className="text-red-300 hover:text-red-200 gap-1"><XCircle size={16} /> Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
